import {
    CLOSE_REASONS,
    DEFAULT_CHILD_CANCEL_TIMEOUT_MS,
    HOST_CHROME_CANCEL_ACTION,
    HOST_CHROME_CANCEL_SOURCE,
    MESSAGE_TYPES,
    RESIZE_THROTTLE_MS,
    UNAUTHORIZED_REFRESH_LIMIT_ERROR,
    createEmbedEnvelope,
    isChildToParentMessage,
    normalizeClosePayload,
    normalizeResizeHeight,
    parseEmbedMessage,
    throttleResize,
} from '../protocol/embedProtocol';

export const createEmbedHost = (getOptions) => {
    let childCancelTimer = null;
    let handshakeTimer = null;
    let handshakeReady = false;
    let initSent = false;
    let readyReceived = false;
    let sessionActive = false;
    let unauthorizedRetryUsed = false;
    let unsubscribe = null;
    let updateIframeHeight = null;
    let waitingForChildClose = false;
    let closeNotified = false;

    const getOpt = () => getOptions() ?? {};

    const clearHandshakeTimer = () => {
        if (!handshakeTimer) return;
        clearTimeout(handshakeTimer);
        handshakeTimer = null;
    };

    const clearChildCancelTimer = () => {
        if (!childCancelTimer) return;
        clearTimeout(childCancelTimer);
        childCancelTimer = null;
    };

    const setHandshakeReady = (nextReady) => {
        handshakeReady = nextReady;
        getOpt().onHandshakeReady?.(nextReady);
    };

    const resetUi = () => {
        initSent = false;
        readyReceived = false;
        unauthorizedRetryUsed = false;
        waitingForChildClose = false;
        closeNotified = false;
        clearHandshakeTimer();
        clearChildCancelTimer();
        setHandshakeReady(false);
        getOpt().onResize?.(null);
        getOpt().onState?.(null);
        getOpt().onStateChange?.(null);
    };

    const sendEnvelope = (type, payload) => {
        return getOpt().sendEnvelope?.(createEmbedEnvelope(type, payload));
    };

    const sendInit = async (request = {}) => {
        const options = getOpt();
        const isUnauthorizedRefresh = request?.reason === 'unauthorized';

        if (isUnauthorizedRefresh && unauthorizedRetryUsed) {
            options.onInitError?.(new Error(UNAUTHORIZED_REFRESH_LIMIT_ERROR));
            return;
        }

        if (isUnauthorizedRefresh) {
            unauthorizedRetryUsed = true;
        }

        let resolvedAuthContext = {};

        if (options.getAuthContext) {
            resolvedAuthContext = await Promise.resolve(
                options.getAuthContext({
                    iframeSrc: options.iframeSrc,
                    initContext: options.initContext ?? {},
                    mode: options.mode,
                    request,
                }),
            );
        }

        const payload = {
            ...(options.mode !== undefined ? { mode: options.mode } : {}),
            ...(options.initExtras ?? {}),
            ...(options.initContext ?? {}),
            ...(resolvedAuthContext ?? {}),
        };

        const sent = sendEnvelope(MESSAGE_TYPES.INIT, payload);
        if (sent === false) return;

        initSent = true;
        options.onEmbedReady?.();
    };

    const notifyClose = (payload) => {
        if (closeNotified) return;

        closeNotified = true;
        waitingForChildClose = false;
        clearChildCancelTimer();
        getOpt().onClose?.(normalizeClosePayload(payload));
    };

    const handleInbound = (raw, meta = {}) => {
        const options = getOpt();
        const data = parseEmbedMessage(raw);
        if (!isChildToParentMessage(data)) return;
        if (options.isInboundTrusted && !options.isInboundTrusted(meta, data)) return;

        switch (data.type) {
            case MESSAGE_TYPES.READY:
                setHandshakeReady(true);
                clearHandshakeTimer();
                if (!readyReceived) {
                    readyReceived = true;
                    sendInit({ reason: 'ready' }).catch((error) => {
                        options.onInitError?.(error);
                    });
                }
                break;
            case MESSAGE_TYPES.REQUEST_CONTEXT_REFRESH:
                sendInit(data.payload ?? { reason: 'contextRefresh' }).catch((error) => {
                    options.onInitError?.(error);
                });
                break;
            case MESSAGE_TYPES.RESIZE: {
                const payload = data.payload;
                const rawHeight =
                    payload && typeof payload === 'object' && 'height' in payload
                        ? payload.height
                        : null;
                const nextHeight = normalizeResizeHeight(rawHeight);
                if (nextHeight !== null) updateIframeHeight?.(nextHeight);
                break;
            }
            case MESSAGE_TYPES.STATE: {
                const nextState = data.payload ?? null;
                options.onState?.(nextState);
                options.onStateChange?.(nextState);
                break;
            }
            case MESSAGE_TYPES.NOTIFY:
                options.onNotify?.(data.payload ?? null);
                break;
            case MESSAGE_TYPES.CLOSE:
                notifyClose(data.payload);
                break;
            default:
                break;
        }
    };

    const syncHandshakeTimer = () => {
        clearHandshakeTimer();
        const options = getOpt();
        const timeoutMs = options.handshakeTimeoutMs;
        if (!sessionActive || !options.iframeSrc || handshakeReady) return;
        if (!timeoutMs || timeoutMs <= 0) return;

        handshakeTimer = setTimeout(() => {
            options.onHandshakeTimeout?.();
        }, timeoutMs);
    };

    const start = () => {
        if (sessionActive) return;

        resetUi();
        sessionActive = true;
        updateIframeHeight = throttleResize((height) => {
            let nextHeight = height;
            const maxIframeHeight = getOpt().maxIframeHeight;
            if (Number.isFinite(maxIframeHeight)) {
                nextHeight = Math.min(nextHeight, maxIframeHeight);
            }
            getOpt().onResize?.(nextHeight);
        }, RESIZE_THROTTLE_MS);
        unsubscribe = getOpt().subscribe?.((raw, meta) => handleInbound(raw, meta)) ?? null;
        syncHandshakeTimer();
    };

    const stop = () => {
        sessionActive = false;
        unsubscribe?.();
        unsubscribe = null;
        updateIframeHeight = null;
        resetUi();
    };

    const sendAction = (action, payload = {}) => {
        if (!action) return false;
        return (
            sendEnvelope(MESSAGE_TYPES.ACTION, {
                action,
                ...(payload ?? {}),
            }) !== false
        );
    };

    const requestChildCancel = (payload = { source: HOST_CHROME_CANCEL_SOURCE }) => {
        if (closeNotified || waitingForChildClose) return false;

        waitingForChildClose = true;
        sendAction(HOST_CHROME_CANCEL_ACTION, payload);
        clearChildCancelTimer();
        childCancelTimer = setTimeout(() => {
            waitingForChildClose = false;
            childCancelTimer = null;
            notifyClose({ reason: CLOSE_REASONS.CANCEL });
        }, getOpt().childCancelTimeoutMs ?? DEFAULT_CHILD_CANCEL_TIMEOUT_MS);

        return true;
    };

    return {
        requestChildCancel,
        reset: resetUi,
        sendAction,
        sendInit,
        start,
        stop,
        syncHandshakeTimer,
    };
};
